import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useApiMutation } from "../../apis/useApi";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import "./PartnerCompaniesPage.css";
import Loader from './custom-loader/Loader';
import NoDataFound from '../../generic-components/NoDataFound/NoDataFound';
import ReadMoreText from '../ReadMoreText/ReadMoreText';
import { LANGUAGE } from '../../utils/languageProperties';

interface Partner {
    id: number;
    name: string;
    logo: string;
    description: string;
    offerCount: number;
}

const PartnerCompaniesPage = () => {
    const navigate = useNavigate();

    const { taxonomyCategoryIds, categoryId, taxonomySubCategoryIds } =
        useParams<{
            taxonomyCategoryIds: string;
            categoryId: string;
            taxonomySubCategoryIds: string;
        }>();

    const [categoryIds, setCategoryIds] = useState<string[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [filteredData, setFilteredData] = useState<Partner[]>([]);

    const { searchValue, setSearchValue } = useOutletContext<any>();

    const scopeKey = 'BID Partner';

    const getTaxonomyCategories = useApiMutation<any, {}>(
        `o/headless-admin-taxonomy/v1.0/taxonomy-categories/${taxonomyCategoryIds}/taxonomy-categories`,
        'get'
    );

    const getPartnerOffers = useApiMutation<any, {}>(
        `o/c/partneroffers/scopes/${scopeKey}`,
        'get'
    );

    const isLoading =
        getTaxonomyCategories?.isLoading || getPartnerOffers?.isLoading;

    const selectedSubService = JSON.parse(
        sessionStorage.getItem('selectedSubServiceItem') || 'null'
    );
    const currentLocale =
        (window as any)?.Liferay?.ThemeDisplay?.getBCP47LanguageId?.() ||
        'en-US';
    const languageId =
        (window as any)?.Liferay?.ThemeDisplay?.getLanguageId?.() || 'en_US';
    const isArabicPortal = String(languageId).toLowerCase().startsWith('ar');

    const parseI18n = (value: any): Record<string, string> => {
        if (!value) return {};

        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch {
                return {};
            }
        }

        return typeof value === 'object' ? value : {};
    };

    const getLocaleValue = (
        value: any,
        localeKeys: string[],
        fallback = ''
    ) => {
        const parsed = parseI18n(value);

        for (const localeKey of localeKeys) {
            if (typeof parsed?.[localeKey] === 'string' && parsed[localeKey]) {
                return parsed[localeKey];
            }

            const matchedKey = Object.keys(parsed).find(
                (key) => key.toLowerCase() === localeKey.toLowerCase()
            );

            if (
                matchedKey &&
                typeof parsed?.[matchedKey] === 'string' &&
                parsed[matchedKey]
            ) {
                return parsed[matchedKey];
            }
        }

        return fallback;
    };

    const getLocalizedText = (i18nValue: any, fallback = '') => {
        const currentLanguage = String(languageId || 'en_US').replace('-', '_');

        return isArabicPortal
            ? getLocaleValue(
                  i18nValue,
                  [
                      currentLanguage,
                      'ar_SA',
                      'ar_AE',
                      'ar_AR',
                      'ar_Ar',
                      'ar_EG',
                      'ar'
                  ],
                  fallback
              )
            : getLocaleValue(
                  i18nValue,
                  [currentLanguage, 'en_US', 'en_EN', 'en_GB', 'en'],
                  fallback
              );
    };

    // STEP 1: Get child categories
    useEffect(() => {
        if (!taxonomyCategoryIds) return;

        getTaxonomyCategories.mutate(
            {
                data: {},
                params: {
                    p_auth: (window as any)?.Liferay?.authToken,
                    restrictFields: 'actions,creator'
                }
            },
            {
                onSuccess: (data) => {
                    const ids = data?.items?.map((item: any) => item.id) || [];
                    setCategoryIds(ids);
                },
                onError: () => setCategoryIds([])
            }
        );
    }, [taxonomyCategoryIds]);

    // STEP 2: Get partner offers
    useEffect(() => {
        if (categoryIds.length === 0) return;

        getPartnerOffers.mutate(
            {
                data: {},
                params: {
                    p_auth: (window as any)?.Liferay?.authToken,
                    pageSize: 0,
                    aggregationTerms: 'r_partnerToOffer_c_corporatePartnerId',
                    nestedFields: 'partnerToOffer',
                    fields: 'partnerToOffer.partnerStatus,partnerToOffer.id,partnerToOffer.companyName,partnerToOffer.companyNameI18n,partnerToOffer.companyLogo,partnerToOffer.address,partnerToOffer.companyOverview,partnerToOffer.companyOverviewI18n',
                    filter: `(offerStatus ne 'suspendedServiceOffer' and offerStatus ne 'deactivatedServiceOffer' and offerStatus ne 'pendingOfferApprovalFromPartner' and offerStatus ne 'pendingOfferApprovalFromAccountManager' and offerStatus ne 'accountManagerNeedRevision' and offerStatus ne 'partnerNeedRevision') and taxonomyCategoryIds/any(t:t eq ${taxonomySubCategoryIds})`
                }
            },
            {
                onSuccess: (data) => {
                    const partnerMap = new Map<number, any>();

                    data?.items?.forEach((item: any) => {
                        const partner = item.partnerToOffer;

                        if (partner) {
                            partnerMap.set(partner.id, {
                                ...partner,
                                offerCount: 0
                            });
                        }
                    });

                    data?.items?.forEach((item: any) => {
                        const partner = item.partnerToOffer;

                        if (partnerMap.has(partner.id)) {
                            partnerMap.get(partner.id).offerCount++;
                        }
                    });

                    const mappedPartners: Partner[] = Array.from(
                        partnerMap.values()
                    ).map((partner: any) => ({
                        id: partner?.id?.toString() || '1',
                        name: getLocalizedText(
                            partner.companyNameI18n,
                            partner.companyName || ''
                        ),
                        logo: partner.companyLogo?.link?.href,
                        description: getLocalizedText(
                            partner.companyOverviewI18n,
                            partner.companyOverview || ''
                        ),
                        offerCount: partner.offerCount
                    }));

                    setPartners(mappedPartners);
                    setFilteredData(mappedPartners);
                }
            }
        );
    }, [categoryIds]);

    useEffect(() => {
        if (searchValue) {
            const filteredData = partners.filter((item) =>
                item.name.toLowerCase().includes(searchValue.toLowerCase())
            );
            setFilteredData(filteredData);
        } else {
            setFilteredData(partners);
        }
    }, [searchValue]);

    return (
        <div className="partner-page">
            <div className="partner-container">
                <Breadcrumbs setSearchValue={setSearchValue} />

                {isLoading ? (
                    <Loader />
                ) : (
                    <div className="partner-content-wrapper">
                        <h2 className="partner-heading">
                            {selectedSubService?.title || ''}
                        </h2>

                        {Boolean(filteredData?.length) ? (
                            <div className="partner-list">
                                {filteredData.map((partner) => (
                                    <div
                                        key={partner.id}
                                        className="partner-card"
                                    >
                                        <img
                                            src={partner.logo}
                                            className="partner-logo"
                                            alt={partner.name}
                                        />

                                        <div className="partner-info">
                                            <h3>{partner.name}</h3>

                                            <ReadMoreText
                                                text={partner.description}
                                                maxLength={400}
                                            />

                                            <span
                                                className="partner-offers"
                                                onClick={() => {
                                                    setSearchValue('');
                                                    sessionStorage.setItem(
                                                        'selectedPartner',
                                                        JSON.stringify({
                                                            id: partner?.id,
                                                            name: partner.name,
                                                            logo:
                                                                partner.logo ??
                                                                '',
                                                            description:
                                                                partner.description,
                                                            locale: currentLocale
                                                        })
                                                    );
                                                    navigate(
                                                        `/categories/${categoryId}/${taxonomyCategoryIds}/${taxonomySubCategoryIds}/${partner.id}`
                                                    );
                                                }}
                                            >
                                                {partner.offerCount}{' '}
                                                {LANGUAGE['offers']}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <NoDataFound />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};;

export default PartnerCompaniesPage;